import { CreationOverlay } from "../misc/CreationOverlay";
import { Plus } from "lucide-react";

import {
  CreateBase,
  Form,
  type Identifier,
  useDataProvider,
  useGetIdentity,
  useGetRecordRepresentation,
  useNotify,
  useRecordContext,
  useTranslate,
} from "ra-core";

import { useCallback, useEffect, useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { SaveButton } from "@/components/admin/form";

import { Button } from "@/components/ui/button";

import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import type { Deal } from "../types";

import { TaskFormContent } from "./TaskFormContent";

export const AddTask = ({
  selectContact,
  display = "chip",
  project,
}: {
  project?: Deal;
  selectContact?: boolean;
  display?: "chip" | "icon" | "button";
}) => {
  const { identity } = useGetIdentity();

  const dataProvider = useDataProvider();

  const notify = useNotify();

  const translate = useTranslate();

  const queryClient = useQueryClient();

  const contact = useRecordContext<any>();

  const getContactRepresentation = useGetRecordRepresentation("contacts");

  const [open, setOpen] = useState(false);

  const [selectedContactIds, setSelectedContactIds] = useState<Identifier[]>(
    [],
  );

  const getInitialContactIds = useCallback((): Identifier[] => {
    if (project) return project.contact_ids ?? [];
    if (!selectContact && contact?.id != null) {
      return [contact.id];
    }

    return [];
  }, [project, selectContact, contact?.id]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedContactIds(getInitialContactIds());
  }, [open, getInitialContactIds]);

  const initialRecord = useMemo(
    () => ({
      type: "",

      status: "new",

      contact_id:
        (project?.id != null || selectContact ? null : contact?.id) ?? null,
      deal_id: project?.id ?? null,

      due_date: new Date(Date.now() + 3600000).toISOString(),

      // Ответственный по умолчанию
      sales_id: project?.sales_id ?? identity?.id,

      // Кто поставил задачу
      created_by_sales_id: identity?.id,
    }),
    // Reopening starts a new draft; contact selection must not reset entered fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      open,
      project?.id,
      project?.sales_id,
      contact?.id,
      selectContact,
      identity?.id,
    ],
  );
  if (!identity) {
    return null;
  }

  const handleOpen = () => {
    setSelectedContactIds(getInitialContactIds());

    setOpen(true);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (nextOpen) {
      setSelectedContactIds(getInitialContactIds());
    }
  };

  const handleSuccess = async (task: any) => {
    let linksFailed = false;

    try {
      if (selectedContactIds.length > 0) {
        const results = await Promise.allSettled(
          selectedContactIds.map((contactId) =>
            dataProvider.create("task_contacts", {
              data: {
                task_id: task.id,

                contact_id: contactId,
              },
            }),
          ),
        );

        linksFailed = results.some((result) => result.status === "rejected");
      }

      /*
       * Пока сохраняем старое поле contact_id
       * для совместимости со старой частью CRM.
       *
       * TaskFormContent уже должен записать туда
       * первый выбранный контакт.
       *
       * Но дополнительно страхуем это здесь.
       */
      const firstContactId = selectedContactIds[0] ?? null;

      if (
        firstContactId != null &&
        String(task.contact_id ?? "") !== String(firstContactId)
      ) {
        try {
          await dataProvider.update("tasks", {
            id: task.id,

            data: {
              contact_id: firstContactId,
            },

            previousData: task,
          });
        } catch (error) {
          console.error("Failed to update legacy contact_id", error);
        }
      }

      /*
       * Обновляем last_seen у всех выбранных контактов.
       * Это старая логика Atomic, которую пока сохраняем.
       */
      if (selectedContactIds.length > 0) {
        await Promise.allSettled(
          selectedContactIds.map(async (contactId) => {
            try {
              const { data: currentContact } = await dataProvider.getOne(
                "contacts",
                {
                  id: contactId,
                },
              );

              if (!currentContact) {
                return;
              }

              await dataProvider.update("contacts", {
                id: contactId,

                data: {
                  last_seen: new Date().toISOString(),
                },

                previousData: currentContact,
              });
            } catch (error) {
              console.error("Failed to update contact last_seen", error);
            }
          }),
        );
      }

      await queryClient.invalidateQueries({
        queryKey: ["task_contacts"],
      });

      await queryClient.invalidateQueries({
        queryKey: ["contact_tasks"],
      });

      await queryClient.invalidateQueries({
        queryKey: ["tasks"],
      });

      await queryClient.invalidateQueries({
        queryKey: ["contacts"],
      });

      setOpen(false);

      setSelectedContactIds([]);

      if (linksFailed) {
        notify("Задача создана, но не все контакты удалось привязать", {
          type: "warning",
        });

        return;
      }

      notify("Задача создана", {
        type: "success",
      });
    } catch (error) {
      console.error("Failed after task creation", error);

      setOpen(false);

      notify("Задача создана, но возникла ошибка при привязке контактов", {
        type: "warning",
      });
    }
  };

  const dialogTitle = project
    ? `Новая задача: ${project.name}`
    : !selectContact && contact
      ? translate("resources.tasks.dialog.create_for", {
          name: getContactRepresentation(contact),
        })
      : translate("resources.tasks.dialog.create");

  return (
    <>
      {display === "icon" || display === "button" ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size={display === "button" ? "default" : "icon"}
                variant={display === "button" ? "default" : "ghost"}
                className="rounded-lg cursor-pointer"
                aria-label={translate("resources.tasks.action.create")}
                onClick={handleOpen}
              >
                {display === "button" ? (
                  "Добавить"
                ) : (
                  <Plus className="size-5" />
                )}
              </Button>
            </TooltipTrigger>

            <TooltipContent>
              {translate("resources.tasks.action.create")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <div className="my-2">
          <Button
            type="button"
            variant="outline"
            className="h-6 cursor-pointer"
            onClick={handleOpen}
            size="sm"
          >
            <Plus className="w-4 h-4" />

            {translate("resources.tasks.action.add")}
          </Button>
        </div>
      )}

      <CreateBase
        resource="tasks"
        record={initialRecord}
        mutationOptions={{
          onSuccess: handleSuccess,
        }}
      >
        <CreationOverlay open={open} onOpenChange={handleOpenChange}>
          <Form className="min-h-0 flex-1 flex flex-col overflow-hidden">
            <DialogHeader className="border-b p-4 pr-12 text-left">
              <DialogTitle className="text-xl font-semibold">
                {dialogTitle}
              </DialogTitle>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <TaskFormContent
                selectedContactIds={selectedContactIds}
                onSelectedContactIdsChange={setSelectedContactIds}
              />
            </div>

            <DialogFooter className="border-t p-4 pb-[max(1rem,env(safe-area-inset-bottom))] w-full justify-end">
              <SaveButton className="w-full h-12" />
            </DialogFooter>
          </Form>
        </CreationOverlay>
      </CreateBase>
    </>
  );
};
